import type { TFunction } from "i18next";
import { useCallback } from "react";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import {
  doGetLotChainGraphData,
  doSaveNodePositions,
  doResetNodePositions,
  doSetVisibleBulk,
  doViewDocument,
  luHandleAppiError,
  type U3141ChildLotChain,
  type U3141NodePositions,
} from "../../../../U3141Service";
import { NS } from "../../../../U3141";

export interface U3141UseLotChainGraphApiHandlersParams {
  t: TFunction;
  toast: { success: (text: string, title?: string) => void; error: (text: string, title?: string) => void };
  loadAndOpenLotChainGraph: (data: U3141ChildLotChain[]) => void;
  getGridData: () => U3141ChildLotChain[];
  refetchMasterLotChain?: () => Promise<void>;
  refreshInitialGraphFromGrid: (data: U3141ChildLotChain[]) => void;
  /** BL-0707 — layout hiérarchique complet (reset / reconstitute). */
  refreshInitialGraphWithFullHierarchyLayout: (data: U3141ChildLotChain[]) => void;
  bumpGraphInitialLayoutKey: () => void;
  isGraphDialogOpen: () => boolean;
}

export interface U3141UseLotChainGraphApiHandlersReturn {
  loadLotChainGraph: (masterLotChainId: string) => Promise<void>;
  /** GET lotChainGraph puis layout hiérarchique U3141 (réinitialiser le layout). */
  reloadLotChainGraphWithAutoLayout: (masterLotChainId: string) => Promise<void>;
  /** BL-0707 — après reconstitute PDF : relayout si dialogue ouvert. */
  reloadLotChainGraphAfterChainStructureChange: (masterLotChainId: string) => Promise<void>;
  saveNodePositions: (masterLotChainId: string, positions: U3141NodePositions) => Promise<boolean>;
  resetNodePositions: (masterLotChainId: string) => Promise<void>;
  onNodeVisibleChange: (params: {
    masterLotChainId: string;
    childLotChainIds: string[];
    visible: boolean;
  }) => Promise<void>;
  /** Récupère l’URL pré-signée du document via l’endpoint générique GET document/:documentId. */
  getDocumentSignUrl: (documentId: string) => Promise<string | null>;
}

/**
 * Hook dédié aux appels API du graphe Chaîne de lots.
 * Encapsule les do* du service et la gestion d'erreurs (pattern obligatoire : pas d'appel do* depuis les composants).
 */
export function useU3141LotChainGraphApiHandlers(
  params: U3141UseLotChainGraphApiHandlersParams
): U3141UseLotChainGraphApiHandlersReturn {
  const {
    t,
    toast,
    loadAndOpenLotChainGraph,
    getGridData,
    refetchMasterLotChain,
    refreshInitialGraphWithFullHierarchyLayout,
    bumpGraphInitialLayoutKey,
    isGraphDialogOpen,
  } = params;

  const axios = useU2000AxiosAuthenticated();

  const handleLotChainGraphFetchError = useCallback(
    (error: unknown, on404: () => void) => {
      const err = error as { response?: { status?: number } };
      if (err.response?.status === 404) {
        on404();
        return;
      }
      const errorResult = U2000HandleApiError(
        error as Parameters<typeof U2000HandleApiError>[0],
        t,
        NS,
        luHandleAppiError,
      );
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
      }
    },
    [t, toast],
  );

  const reloadLotChainGraphWithAutoLayout = useCallback(
    async (masterLotChainId: string) => {
      try {
        const data = await doGetLotChainGraphData(axios, masterLotChainId);
        refreshInitialGraphWithFullHierarchyLayout(data);
      } catch (error) {
        handleLotChainGraphFetchError(error, () => {
          refreshInitialGraphWithFullHierarchyLayout(getGridData());
        });
      }
    },
    [axios, getGridData, handleLotChainGraphFetchError, refreshInitialGraphWithFullHierarchyLayout],
  );

  const reloadLotChainGraphAfterChainStructureChange = useCallback(
    async (masterLotChainId: string) => {
      if (!isGraphDialogOpen()) {
        return;
      }
      try {
        const data = await doGetLotChainGraphData(axios, masterLotChainId);
        refreshInitialGraphWithFullHierarchyLayout(data);
        bumpGraphInitialLayoutKey();
      } catch (error) {
        handleLotChainGraphFetchError(error, () => {
          refreshInitialGraphWithFullHierarchyLayout(getGridData());
          bumpGraphInitialLayoutKey();
        });
      }
    },
    [
      axios,
      bumpGraphInitialLayoutKey,
      getGridData,
      handleLotChainGraphFetchError,
      isGraphDialogOpen,
      refreshInitialGraphWithFullHierarchyLayout,
    ],
  );

  const loadLotChainGraph = useCallback(
    async (masterLotChainId: string) => {
      try {
        const data = await doGetLotChainGraphData(axios, masterLotChainId);
        loadAndOpenLotChainGraph(data);
      } catch (error) {
        handleLotChainGraphFetchError(error, () => {
          loadAndOpenLotChainGraph(getGridData());
        });
      }
    },
    [axios, getGridData, handleLotChainGraphFetchError, loadAndOpenLotChainGraph],
  );

  const saveNodePositions = useCallback(
    async (masterLotChainId: string, positions: U3141NodePositions): Promise<boolean> => {
      try {
        await doSaveNodePositions(axios, masterLotChainId, positions);
        toast.success(t("successNodePositionsSaved", { ns: NS }), t("successTitle", { ns: NS }));
        await refetchMasterLotChain?.();
        return true;
      } catch (error) {
        const errorResult = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
        }
        return false;
      }
    },
    [axios, t, toast, refetchMasterLotChain]
  );

  const resetNodePositions = useCallback(
    async (masterLotChainId: string) => {
      try {
        await doResetNodePositions(axios, masterLotChainId);
        toast.success(t("successNodePositionsReset", { ns: NS }), t("successTitle", { ns: NS }));
        await refetchMasterLotChain?.();
        await reloadLotChainGraphWithAutoLayout(masterLotChainId);
        bumpGraphInitialLayoutKey();
      } catch (error) {
        const errorResult = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
        }
      }
    },
    [axios, t, toast, refetchMasterLotChain, reloadLotChainGraphWithAutoLayout, bumpGraphInitialLayoutKey]
  );

  const onNodeVisibleChange = useCallback(
    async (params: { masterLotChainId: string; childLotChainIds: string[]; visible: boolean }) => {
      if (!params.masterLotChainId || !params.childLotChainIds?.length) return;
      try {
        await doSetVisibleBulk(
          axios,
          params.masterLotChainId,
          params.childLotChainIds,
          params.visible
        );
        toast.success(t("successVisible", { ns: NS }), t("successTitle", { ns: NS }));
      } catch (error) {
        const errorResult = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
        }
      }
    },
    [axios, t, toast]
  );

  const getDocumentSignUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      try {
        const res = await doViewDocument(axios, documentId);
        const data = res.data as { signUrl?: string; result?: { signUrl?: string } } | undefined;
        return data?.signUrl ?? data?.result?.signUrl ?? null;
      } catch (error) {
        const errorResult = U2000HandleApiError(
          error as Parameters<typeof U2000HandleApiError>[0],
          t,
          NS,
          luHandleAppiError
        );
        // N'afficher le toast que si l'onglet est visible (évite erreurs en arrière-plan).
        if (errorResult.genericErrors?.length && document.visibilityState === "visible") {
          errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
        }
        return null;
      }
    },
    [axios, t, toast]
  );

  return {
    loadLotChainGraph,
    reloadLotChainGraphWithAutoLayout,
    reloadLotChainGraphAfterChainStructureChange,
    saveNodePositions,
    resetNodePositions,
    onNodeVisibleChange,
    getDocumentSignUrl,
  };
}
